// ===================================
// KAIXO - App Principal
// ===================================

import React, { useState, useEffect, useRef } from 'react';
import Login from 'components/Login/Login';
import ConversationList from 'components/ConversationList/ConversationList';
import ChatWindow from 'components/ChatWindow/ChatWindow';
import ChannelsV2Modal from 'components/ChannelsV2/ChannelsV2Modal';
import CRMModal from 'components/CRM/CRMModal';
import { connectSocket, onNewMessage, onNewGroupMessageGlobal, onGroupAvatarUpdated, onAddedToGroup, onUserProfileUpdated } from './services/socket';
import api from './services/api';
import { hasImpulsaAccess } from './utils/access';
import { registerPushNotifications } from './services/pushNotifications';
import { registerFCMNotifications, clearFCMNotifications } from './services/fcmNotifications';
import NotificationToast from './components/NotificationToast/NotificationToast';
import Onboarding from './components/Onboarding/Onboarding';
import ShareReceiverModal from './components/ShareReceiverModal/ShareReceiverModal';
import UpgradeModal from './components/Plans/UpgradeModal';
import UpdateBanner from './components/UpdateBanner/UpdateBanner';
// Auto-update OTA (cambios de frontend sin reinstalar APK).
import VersionUpdateBanner from './components/VersionUpdateBanner/VersionUpdateBanner';
// Banner de estado del plan (expirando / expirado / pago fallido).
import PlanStatusBanner from './components/PlanStatusBanner/PlanStatusBanner';
import CallManager from './components/Calls/CallManager';
import GroupCallManager from './components/GroupCalls/GroupCallManager';
import PullToRefresh from './components/PullToRefresh/PullToRefresh';
import { playMessageSound } from './utils/messageSound';
import './App.css';

// Sistema teclado: NATIVO PURO (decisión Víctor 2026-05-07 tras 4 horas
// de iteraciones fallidas). Toda la lógica JS/CSS custom de keyboard se
// eliminó. AndroidManifest sigue con adjustResize (default WebView).
// Si en el futuro hay que abordar el bug de algún WebView específico,
// se hará con device físico para iterar con datos directos, no parches.


// Importación dinámica del plugin SendIntent (solo nativo)
let SendIntent = null;
try {
    // eslint-disable-next-line global-require, import/no-unresolved
    SendIntent = require('send-intent').SendIntent;
} catch { /* no disponible en web */ }

// Logging persistente para debug (especialmente útil cuando DevTools se
// desconecta al perder foco la app, p.ej. al venir de un share intent).
// Lectura desde DevTools: console.table(JSON.parse(localStorage.getItem('kaixo_debug_log')||'[]'))
function kaixoDebugLog(key, data) {
    try {
        const log = JSON.parse(localStorage.getItem('kaixo_debug_log') || '[]');
        let payload;
        try {
            payload = typeof data === 'string' ? data : JSON.stringify(data);
        } catch {
            payload = String(data);
        }
        log.push({ ts: new Date().toISOString(), key, data: payload });
        // Mantener solo las últimas 100 entradas
        while (log.length > 100) log.shift();
        localStorage.setItem('kaixo_debug_log', JSON.stringify(log));
        // También a console por si está conectado
        try { console.log('[kaixoDebug]', key, data); } catch {}
    } catch { /* defensive */ }
}
// Exponer en window para inspección manual desde DevTools
if (typeof window !== 'undefined') window.kaixoDebugLog = kaixoDebugLog;

// Hidratación de JWT desde URL fragment cuando el usuario llega desde
// la página de pairing kaixo.ai/web (vinculación móvil ↔ web). El
// fragmento de URL (#token=...) NO viaja al servidor — privacy-friendly.
// Se ejecuta antes de cualquier componente React para que el JWT esté
// en localStorage antes de que App lea sesión.
(function hydrateTokenFromHash() {
    if (typeof window === 'undefined') return;
    try {
        const m = (window.location.hash || '').match(/#token=([^&]+)/);
        if (!m) return;
        const jwt = decodeURIComponent(m[1]);
        if (jwt && jwt.split('.').length === 3) {
            localStorage.setItem('kaixo_token', jwt);
            // Limpiar URL para que el JWT no aparezca en la barra de
            // direcciones ni en historial. replaceState evita añadir
            // entrada al historial.
            try {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
            } catch {}
        }
    } catch {}
})();

// Detección de altura de la barra de navegación de Android (3 botones).
// En algunos dispositivos `env(safe-area-inset-bottom)` devuelve 0 aunque
// haya barra de nav, y el input del chat queda tapado. Comparamos
// screen.height con innerHeight ANTES de que se abra el teclado: si la
// diferencia está entre 30 y 100px es típicamente la barra de nav.
// Lo cacheamos en una CSS variable --kaixo-nav-bar que los componentes
// usan vía max() junto a env() para que funcione en cualquier device.
(function detectAndroidNavBar() {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    try {
        const isCapacitor = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
        const platform = isCapacitor ? window.Capacitor.getPlatform() : null;
        if (platform === 'android') {
            document.body.classList.add('kaixo-platform-android');
        } else if (platform === 'ios') {
            document.body.classList.add('kaixo-platform-ios');
        }
        if (platform !== 'android') {
            document.documentElement.style.setProperty('--kaixo-nav-bar', '0px');
            return;
        }
        const screenH = window.screen?.height || 0;
        const innerH = window.innerHeight || 0;
        const diff = screenH - innerH;
        // 30-100px = barra 3 botones (~48px) o gestos en algunos OEMs
        const navBar = (diff > 30 && diff < 100) ? Math.min(diff, 60) : 0;
        document.documentElement.style.setProperty('--kaixo-nav-bar', navBar + 'px');
    } catch {
        document.documentElement.style.setProperty('--kaixo-nav-bar', '0px');
    }
})();

function App() {
    // Aplicar modo oscuro al arrancar si estaba guardado
    if (typeof document !== 'undefined' && localStorage.getItem('kaixo_dark_mode') === '1') {
        document.body.classList.add('dark-mode');
    }

    // Detectar invitación a canal vía URL /c/<código> o post /p/<id>
    if (typeof window !== 'undefined') {
        const mC = window.location.pathname.match(/^\/c\/(@?[a-zA-Z0-9_-]+)$/);
        if (mC) {
            try { localStorage.setItem('kaixo_pending_channel', mC[1]); } catch {}
        }
        const mP = window.location.pathname.match(/^\/p\/([a-fA-F0-9]{24})$/);
        if (mP) {
            try { localStorage.setItem('kaixo_pending_post', mP[1]); } catch {}
        }
    }

    const [currentUser, setCurrentUser] = useState(() => {
        const saved = localStorage.getItem('kaixo_user');
        return saved ? JSON.parse(saved) : null;
    });
    const [selectedConversation, setSelectedConversation] = useState(null);
    // Panel activo en la columna2 cuando NO hay chat seleccionado.
    // 'chats' (por defecto), 'latidos', 'crm'. En web (>768px) abrir Latidos
    // o Impulsa rellena la columna2 sin tapar la lista de la columna1.
    // En móvil (≤768px) ConversationList se hace cargo y los embeds vuelven
    // dentro de su lista como antes (compat).
    const [activePanel, setActivePanel] = useState('chats');
    // Reset del panel activo si cambia la identidad del usuario (logout
    // + login con otra cuenta). Sin esto, si estabas en Impulsa y cambias
    // de user, el panel sigue abierto pero los datos son del anterior
    // hasta refrescar.
    const currentUserIdForPanel = currentUser?._id;
    useEffect(() => {
        setActivePanel('chats');
    }, [currentUserIdForPanel]);

    // Tap en notificación FCM de llamada → asegura que la app está en
    // pestaña Chats y reenvía a abrir el chat correspondiente. La
    // socket re-conecta sola al volver a foreground; si la llamada
    // sigue ringando el backend reemite `incoming_call` y CallManager
    // lo recoge. Si la llamada ya terminó, al menos el usuario aterriza
    // en el chat de quien le llamó.
    useEffect(() => {
        const onOpenCall = (e) => {
            setActivePanel('chats');
            const { conversationId, groupId } = e.detail || {};
            if (conversationId || groupId) {
                // Pequeño retraso para que la pestaña esté montada antes
                // de que ConversationList procese el deep-link.
                setTimeout(() => {
                    try { window.dispatchEvent(new CustomEvent('kaixo:open_chat', { detail: { conversationId, groupId } })); } catch {}
                }, 100);
            }
        };
        window.addEventListener('kaixo:open_call', onOpenCall);
        // Mismo handler para chats normales: navegar a tab chats antes
        // de que ConversationList haga el select. Sin esto, si la app
        // estaba en Impulsa o Latidos cuando llegó el push, el chat
        // se abre detrás del panel actual.
        const onOpenChat = () => setActivePanel('chats');
        window.addEventListener('kaixo:open_chat', onOpenChat);
        return () => {
            window.removeEventListener('kaixo:open_call', onOpenCall);
            window.removeEventListener('kaixo:open_chat', onOpenChat);
        };
    }, []);
    const [isWideScreen, setIsWideScreen] = useState(() => {
        try { return window.matchMedia('(min-width: 769px)').matches; } catch { return false; }
    });
    useEffect(() => {
        let mq;
        try {
            mq = window.matchMedia('(min-width: 769px)');
            const handler = (e) => setIsWideScreen(e.matches);
            mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
            return () => mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
        } catch {}
    }, []);
    const [refreshKey, setRefreshKey] = useState(0);
    const [onlineUsers, setOnlineUsers] = useState(new Set());
    const [notifications, setNotifications] = useState([]);
    const [sharePayload, setSharePayload] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState({ connected: true });

    useEffect(() => {
        const handleStatus = (e) => setConnectionStatus(e.detail);
        window.addEventListener('socket_status', handleStatus);
        return () => window.removeEventListener('socket_status', handleStatus);
    }, []);

    // Link /c/<código>: abrir perfil público del canal, no auto-suscribir.
    // El usuario decide suscribirse desde el perfil.
    const subscribePending = React.useCallback(() => {
        if (!currentUser) return;
        const code = localStorage.getItem('kaixo_pending_channel');
        const postId = localStorage.getItem('kaixo_pending_post');

        if (postId) {
            localStorage.removeItem('kaixo_pending_post');
            if (window.history && window.location.pathname.startsWith('/p/')) {
                window.history.replaceState({}, '', '/');
            }
            window.dispatchEvent(new CustomEvent('open_post', { detail: { messageId: postId } }));
            return;
        }

        if (!code) return;
        localStorage.removeItem('kaixo_pending_channel');
        if (window.history && window.location.pathname.startsWith('/c/')) {
            window.history.replaceState({}, '', '/');
        }
        window.dispatchEvent(new CustomEvent('open_channel_profile', { detail: { inviteCode: code } }));
    }, [currentUser]);

    useEffect(() => { subscribePending(); }, [subscribePending]);

    useEffect(() => {
        const handler = () => subscribePending();
        window.addEventListener('subscribe_pending_channel', handler);
        return () => window.removeEventListener('subscribe_pending_channel', handler);
    }, [subscribePending]);
    const selectedConversationRef = useRef(null);
    const currentUserRef = useRef(null);
    useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

    const handleLogin = (user) => {
        try {
            localStorage.setItem('kaixo_user', JSON.stringify(user));
            setCurrentUser(user);
            setTimeout(() => {
                try {
                    connectSocket(
                        user._id,
                        (data) => setOnlineUsers(prev => new Set([...prev, data.userId])),
                        (data) => setOnlineUsers(prev => { const next = new Set(prev); next.delete(data.userId); return next; }),
                        (data) => setOnlineUsers(new Set(data.userIds))
                    );
                } catch(e) {
                    console.error('Socket error:', e);
                }
            }, 500);
        } catch(e) {
            console.error('Login error:', e);
        }
    };

    useEffect(() => {
        const handleUserUpdated = (e) => {
            setCurrentUser(e.detail);
        };
        window.addEventListener('user_updated', handleUserUpdated);
        return () => window.removeEventListener('user_updated', handleUserUpdated);
    }, []);

    // Sync en tiempo real entre sesiones del mismo user (móvil ↔ web).
    // El backend emite `user_profile_updated` por socket cuando alguien
    // actualiza su perfil; si el userId coincide con el nuestro, mergeamos
    // los campos cambiados en currentUser y persistimos en localStorage.
    // Sin esto, los cambios en web no se reflejaban en móvil hasta el
    // próximo focus/resume (y viceversa).
    useEffect(() => {
        if (!currentUser?._id) return;
        const myId = String(currentUser._id);
        const cleanup = onUserProfileUpdated((data) => {
            if (!data || String(data.userId) !== myId) return;
            setCurrentUser(prev => {
                if (!prev) return prev;
                const next = {
                    ...prev,
                    name: data.name ?? prev.name,
                    avatar: data.avatar ?? prev.avatar,
                    status: data.status ?? prev.status,
                    language: data.language ?? prev.language,
                    region: data.region ?? prev.region,
                    country: data.country ?? prev.country,
                    accountType: data.accountType ?? prev.accountType,
                    plan: data.plan ?? prev.plan
                };
                try { localStorage.setItem('kaixo_user', JSON.stringify(next)); } catch {}
                return next;
            });
        });
        return cleanup;
    }, [currentUser?._id]);

    // visibilitychange: cuando el usuario vuelve a la pestaña (en web),
    // forzar un re-fetch de /users/me. Cubre el caso "cambio de pestaña
    // dentro del mismo navegador" donde `focus` NO se dispara.
    useEffect(() => {
        if (!currentUser?._id) return;
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            api.get('/users/me').then(r => {
                const fresh = r?.data?.user;
                if (!fresh) return;
                setCurrentUser(prev => {
                    if (!prev) return prev;
                    const merged = { ...prev, ...fresh };
                    try { localStorage.setItem('kaixo_user', JSON.stringify(merged)); } catch {}
                    return merged;
                });
            }).catch(() => {});
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [currentUser?._id]);

    // Hidratación + refresh del user al arrancar la app. Dos casos:
    //   1) Hay JWT pero NO hay user (ej. vinculación QR Web): fetch a
    //      /users/me y poblar via handleLogin (conecta socket, etc.).
    //   2) Hay JWT Y hay user en localStorage: hacer ANYWAY el fetch
    //      para refrescar campos que pueden haber cambiado en otra
    //      sesión mientras la app estaba cerrada (cambio de accountType
    //      desde web, plan upgrade, avatar nuevo, etc.). Cold-start
    //      sin focus/resume → este efecto es la única forma de sync.
    //   Si el token es inválido (401) lo limpia para no loopear.
    useEffect(() => {
        const token = localStorage.getItem('kaixo_token');
        if (!token) return;
        let cancelled = false;
        api.get('/users/me')
            .then(({ data }) => {
                if (cancelled || !data?.user) return;
                const fresh = data.user;
                try { localStorage.setItem('kaixo_user', JSON.stringify(fresh)); } catch {}
                if (currentUser) {
                    // Caso 2: ya había user — solo mergear campos cambiados.
                    // No llamamos handleLogin para no reabrir socket de cero.
                    setCurrentUser(prev => prev ? ({ ...prev, ...fresh }) : fresh);
                } else {
                    // Caso 1: primer login en esta sesión → flujo completo
                    handleLogin(fresh);
                }
            })
            .catch((err) => {
                if (err?.response?.status === 401) {
                    try { localStorage.removeItem('kaixo_token'); } catch {}
                }
                console.warn('[App] /users/me failed during hydration:', err?.message || err);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Registrar push notifications al hacer login
    useEffect(() => {
        if (currentUser?._id) {
            registerPushNotifications(currentUser._id);
            registerFCMNotifications(currentUser._id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?._id]);

    // Detectar share entrante (otra app comparte a KAIXO)
    useEffect(() => {
        if (!SendIntent || !currentUser?._id) return;
        // Llevamos control de qué intent ya procesamos para evitar reabrir
        // el modal cuando la app vuelve al foreground (no llamamos
        // SendIntent.finish() porque eso cierra KAIXO si fue cold-launched).
        const processedKey = (r) => `${r?.type || ''}|${r?.url || ''}|${r?.description || ''}|${r?.title || ''}`;
        let lastProcessed = '';
        const check = async (attempt = 0) => {
            try {
                const result = await SendIntent.checkSendIntentReceived();
                kaixoDebugLog('share.intent.result', { ...result, attempt });

                // El plugin send-intent v7 a veces emite el evento ANTES de
                // que la URI del archivo esté resuelta. Si el share es de
                // media (image/video) pero llega sin URL, reintentamos hasta
                // 10 veces con 200ms de espera — total max 2s. Sin esto, el
                // modal se abría con URL vacía y al enviar se mandaba un
                // texto en blanco antes de que llegase la foto.
                const isMedia = result?.type && (
                    result.type.startsWith('image/') ||
                    result.type.startsWith('video/')
                );
                const hasUrl = !!(result?.url && result.url.length > 0);
                const hasText = !!(result?.description || result?.title);

                if (isMedia && !hasUrl && attempt < 10) {
                    setTimeout(() => check(attempt + 1), 200);
                    return;
                }

                if (hasUrl || (!isMedia && hasText)) {
                    const key = processedKey(result);
                    if (key === lastProcessed) {
                        kaixoDebugLog('share.intent.duplicate_ignored', { key: key.slice(0, 80) });
                        return;
                    }
                    lastProcessed = key;
                    setSharePayload({
                        type: result.type || 'text/plain',
                        url: result.url || '',
                        title: result.title || '',
                        description: result.description || ''
                    });
                } else {
                    kaixoDebugLog('share.intent.empty', { hasResult: !!result, keys: result ? Object.keys(result) : [], attempt });
                }
            } catch (e) {
                kaixoDebugLog('share.intent.check_error', e?.message);
            }
        };
        check();
        // Re-chequear al volver al foreground (cuando otra app despierta Kaixo via SEND)
        const onResume = () => check();
        document.addEventListener('resume', onResume);
        window.addEventListener('sendIntentReceived', onResume);
        return () => {
            document.removeEventListener('resume', onResume);
            window.removeEventListener('sendIntentReceived', onResume);
        };
    }, [currentUser?._id]);

    useEffect(() => {
        const handleBackButton = () => {
            if (selectedConversation) {
                setSelectedConversation(null);
                setRefreshKey(prev => prev + 1);
            }
        };
        document.addEventListener('backbutton', handleBackButton);
        return () => document.removeEventListener('backbutton', handleBackButton);
    }, [selectedConversation]);

    useEffect(() => {
        if (currentUser) {
            connectSocket(
                currentUser._id,
                (data) => setOnlineUsers(prev => new Set([...prev, data.userId])),
                (data) => setOnlineUsers(prev => { const next = new Set(prev); next.delete(data.userId); return next; }),
                (data) => setOnlineUsers(new Set(data.userIds))
            );
            console.log('✅ Usuario conectado:', currentUser.name);
        }

        const handleResume = () => {
            if (currentUser) {
                console.log('📱 App resumida — reconectando...');
                clearFCMNotifications();
                connectSocket(
                    currentUser._id,
                    (data) => setOnlineUsers(prev => new Set([...prev, data.userId])),
                    (data) => setOnlineUsers(prev => { const next = new Set(prev); next.delete(data.userId); return next; }),
                    (data) => setOnlineUsers(new Set(data.userIds))
                );

                // Re-fetch del propio user para detectar cambios hechos
                // desde otra sesión (ej. web cambia accountType o avatar).
                // Ignoramos errores silenciosamente — si falla, el user
                // mantiene los datos en cache local hasta el siguiente resume.
                api.get('/users/me').then(r => {
                    if (r?.data?.user) {
                        const fresh = r.data.user;
                        // Solo actualizar si hay diferencias relevantes para
                        // evitar re-renders innecesarios. Comparamos campos
                        // que afectan a la UI (accountType, plan, isAdmin, etc.)
                        const localCacheStr = JSON.stringify({
                            accountType: currentUser.accountType,
                            plan: currentUser.plan,
                            isAdmin: currentUser.isAdmin,
                            language: currentUser.language,
                            region: currentUser.region,
                            avatar: currentUser.avatar,
                            name: currentUser.name
                        });
                        const freshStr = JSON.stringify({
                            accountType: fresh.accountType,
                            plan: fresh.plan,
                            isAdmin: fresh.isAdmin,
                            language: fresh.language,
                            region: fresh.region,
                            avatar: fresh.avatar,
                            name: fresh.name
                        });
                        if (localCacheStr !== freshStr) {
                            console.log('📥 User refrescado desde backend — cambios detectados');
                            setCurrentUser(prev => ({ ...prev, ...fresh }));
                            try { localStorage.setItem('kaixo_user', JSON.stringify({ ...currentUser, ...fresh })); } catch {}
                        }
                    }
                }).catch(() => {});
            }
        };

        document.addEventListener('resume', handleResume);
        window.addEventListener('focus', handleResume);

        return () => {
            document.removeEventListener('resume', handleResume);
            window.removeEventListener('focus', handleResume);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Registrar listeners globales de socket
        // onNewMessage/onNewGroupMessage ya usan socket.off antes de socket.on
        // así que pueden llamarse varias veces sin duplicar
        const registerListeners = () => {
            onNewMessage((data) => {
                window.dispatchEvent(new Event('conversations_updated'));
                const current = selectedConversationRef.current;
                const isSameConv = current?.id?.toString() === data.conversationId?.toString();
                // Mensaje del propio usuario en otro dispositivo: no sonar.
                const senderId = data.message?.sender?.id?.toString();
                const myId = currentUserRef.current?._id?.toString();
                const isFromMe = senderId && myId && senderId === myId;
                if (!isSameConv && data.message && !isFromMe) {
                    // Si el receptor tiene esta conversación silenciada,
                    // ni sonido ni toast — debe ser silencio total.
                    if (!data.isMuted) {
                        playMessageSound();
                        const notifId = Date.now();
                        setNotifications(prev => [...prev, {
                            id: notifId,
                            senderName: data.message.sender?.name || 'Nuevo mensaje',
                            avatar: data.message.sender?.avatar || '',
                            preview: data.message.text || '📎 Archivo',
                            conversationId: data.conversationId,
                            type: 'direct',
                            data: data
                        }]);
                        setTimeout(() => {
                            setNotifications(prev => prev.filter(n => n.id !== notifId));
                        }, 4000);
                    }
                }
            });

            onNewGroupMessageGlobal((data) => {
                window.dispatchEvent(new Event('conversations_updated'));
                const current = selectedConversationRef.current;
                const isSameGroup = current?.id?.toString() === data.groupId?.toString();
                const gSenderId = data.message?.sender?.id?.toString();
                const gMyId = currentUserRef.current?._id?.toString();
                const gIsFromMe = gSenderId && gMyId && gSenderId === gMyId;
                if (!isSameGroup && data.message && !data.message.isSystemMessage && !gIsFromMe && !data.isMuted) {
                    playMessageSound();
                    const msgId = data.message.id?.toString();
                    const notifId = Date.now();
                    setNotifications(prev => {
                        if (prev.some(n => n.msgId === msgId)) return prev;
                        return [...prev, {
                            id: notifId,
                            msgId,
                            senderName: data.message.sender?.name || 'Nuevo mensaje',
                            avatar: data.message.sender?.avatar || '',
                            preview: data.message.text || '📎 Archivo',
                            groupId: data.groupId,
                            type: 'group',
                            data: data
                        }];
                    });
                    setTimeout(() => {
                        setNotifications(prev => prev.filter(n => n.id !== notifId));
                    }, 4000);
                }
            });

            onGroupAvatarUpdated((data) => {
                window.dispatchEvent(new Event('conversations_updated'));
                setSelectedConversation(prev => {
                    if (prev?.id?.toString() === data.groupId?.toString()) {
                        return { ...prev, avatar: data.avatar };
                    }
                    return prev;
                });
            });

            onAddedToGroup((data) => {
                setTimeout(() => {
                    window.dispatchEvent(new Event('conversations_updated'));
                }, 500);
                window.dispatchEvent(new CustomEvent('show_notification', {
                    detail: {
                        message: `Has sido añadido al grupo "${data.groupName}"`,
                        groupId: data.groupId
                    }
                }));
            });

            onUserProfileUpdated((data) => {
                window.dispatchEvent(new Event('conversations_updated'));
                setSelectedConversation(prev => {
                    if (!prev || prev.type === 'group') return prev;
                    if (prev.otherUser?.id?.toString() === data.userId?.toString()) {
                        return {
                            ...prev,
                            otherUser: {
                                ...prev.otherUser,
                                name: data.name,
                                avatar: data.avatar,
                                status: data.status,
                                language: data.language,
                                region: data.region
                            }
                        };
                    }
                    return prev;
                });
            });
        };

        // Registrar ahora (si el socket ya está listo) y al evento socket_ready
        registerListeners();
        window.addEventListener('socket_ready', registerListeners);
        return () => window.removeEventListener('socket_ready', registerListeners);
    }, []);

    const handleSelectConversation = (conversation) => {
        setSelectedConversation(conversation);
        selectedConversationRef.current = conversation;
        const name = conversation.type === 'group'
            ? conversation.name
            : conversation.otherUser.name;
        console.log('💬 Conversación/Grupo seleccionado:', name);
    };

    if (!currentUser) {
        return <Login onLogin={handleLogin} />;
    }

    // Onboarding: si el usuario no tiene accountType configurado
    const onboardingDone = localStorage.getItem('kaixo_onboarding_done') === '1' && currentUser?.accountType;
    if (!onboardingDone) {
        return <Onboarding onComplete={(type, interests) => {
            if (type && currentUser._id) {
                const token = localStorage.getItem('kaixo_token') || '';
                const body = { userId: currentUser._id, accountType: type };
                if (Array.isArray(interests)) body.interests = interests;
                fetch(`${process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api'}/users/profile`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(body)
                }).catch(() => {});
                const updated = { ...currentUser, accountType: type, interests: interests || [] };
                localStorage.setItem('kaixo_user', JSON.stringify(updated));
                setCurrentUser(updated);
            }
        }} />;
    }

    return (
        <PullToRefresh onRefresh={() => window.location.reload()}>
        <div className="App">
            {/* Banner de actualización disponible / overlay de update forzoso (APK nativo) */}
            <UpdateBanner />
            {/* Auto-actualización OTA: detecta nuevo bundle frontend en Render
                y recarga la app sola (sin reinstalar APK ni perder credenciales). */}
            <VersionUpdateBanner />
            {/* Banner de estado del plan (expirando / expirado / pago fallido).
                Solo aparece si hay algo que avisar. Se puede dismiss 24h. */}
            {currentUser && <PlanStatusBanner />}
            {/* Modal global que aparece cuando el backend devuelve plan_limit_exceeded */}
            <UpgradeModal />
            {/* Gestor global de llamadas: escucha socket + eventos DOM y
                muestra los modales de llamada entrante/saliente/activa */}
            {currentUser && <CallManager currentUser={currentUser} />}
            {/* Llamadas grupales: módulo aislado. Escucha 'incoming_group_call'
                en socket y window 'kaixo:group_call_start' lanzado desde el
                chat para iniciar. No interfiere con CallManager (1-a-1). */}
            {currentUser && <GroupCallManager currentUser={currentUser} />}
            {!connectionStatus.connected && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
                    background: connectionStatus.reconnecting ? '#F5A000' : '#e53935',
                    color: 'white', textAlign: 'center', padding: '6px',
                    fontSize: '13px', fontFamily: 'Nunito, sans-serif', fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                    {connectionStatus.reconnecting
                        ? `🔄 Reconectando${connectionStatus.attempt ? ` (intento ${connectionStatus.attempt})` : '...'}`
                        : '⚠️ Sin conexión con el servidor'}
                </div>
            )}
            {sharePayload && (
                <ShareReceiverModal
                    payload={sharePayload}
                    currentUser={currentUser}
                    onClose={() => setSharePayload(null)}
                    onComplete={() => setSharePayload(null)}
                />
            )}
            <NotificationToast
                notifications={notifications}
                onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
                onSelect={(notif) => {
                    if (notif.type === 'group') {
                        window.dispatchEvent(new CustomEvent('select_group', { detail: { groupId: notif.groupId } }));
                    } else {
                        window.dispatchEvent(new CustomEvent('select_conversation', { detail: { conversationId: notif.conversationId } }));
                    }
                }}
            />
            <div className={`main-layout ${selectedConversation ? 'chat-active' : ''} ${isWideScreen ? 'wide' : ''} panel-${activePanel}`}>
                {/* La columna 1 sigue siendo la ConversationList con su header
                    + tabs. Cuando estamos en Impulsa en web, internamente
                    cambia su body por el sidebar de notas (sin tapar el header
                    para que el usuario pueda volver a Chats / Latidos). */}
                <ConversationList
                    currentUser={currentUser}
                    onSelectConversation={(conv) => {
                        setActivePanel('chats');
                        handleSelectConversation(conv);
                    }}
                    refreshKey={refreshKey}
                    onlineUsers={onlineUsers}
                    activeTab={isWideScreen ? activePanel : undefined}
                    onTabChange={isWideScreen ? (tab) => {
                        setActivePanel(tab);
                        if (tab !== 'chats') setSelectedConversation(null);
                    } : undefined}
                    embeddedDisabled={isWideScreen}
                    showCrmSidebarInWeb={isWideScreen && activePanel === 'crm' && hasImpulsaAccess(currentUser)}
                />
                {/* Columna 2: en web puede mostrar Latidos, CRM/Impulsa o el chat.
                    En móvil siempre es ChatWindow (los embeds viven dentro de
                    ConversationList por compat). */}
                {isWideScreen && activePanel === 'latidos' && !selectedConversation ? (
                    <ChannelsV2Modal
                        currentUser={currentUser}
                        onClose={() => setActivePanel('chats')}
                        embedded={true}
                    />
                ) : isWideScreen && activePanel === 'crm' && !selectedConversation && hasImpulsaAccess(currentUser) ? (
                    /* Web: el mismo CRMModal embedded que en móvil rellena
                       la columna2. Dentro del CRMModal, la sub-pestaña
                       "Contactos" detecta web y muestra el kanban; las
                       demás secciones (dashboard, reports, etc.) se
                       comparten 1:1 entre web y móvil. */
                    <CRMModal
                        currentUser={currentUser}
                        onClose={() => setActivePanel('chats')}
                        embedded={true}
                    />
                ) : (
                    <ChatWindow
                        currentUser={currentUser}
                        conversation={selectedConversation}
                        onlineUsers={onlineUsers}
                        onBack={() => {
                            setSelectedConversation(null);
                            setRefreshKey(prev => prev + 1);
                        }}
                    />
                )}
            </div>
        </div>
        </PullToRefresh>
    );
}

export default App;